import { Fragment, useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Progress,
  Row,
} from 'reactstrap';
import { formatCurrency, toMoneyWhole } from '~/services/helper';
import { getDashColor, renderIcon } from '~/shared/components/icons';
import { CampaignSerializer, IFundProgress } from '~/types/campaign';
import { CampaignUpdateModal } from '../../Campaign/components/CampaignUpdateModal';
import { postCampaignUpdate } from '~/services/campaign';
import { useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';
import { IconVerified } from '~/shared/components/icons';
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  FacebookIcon,
  XIcon,
  LinkedinIcon,
} from 'react-share';

export interface ProgressCardProps {
  campaign: CampaignSerializer;
  progress: IFundProgress;
}

const fontcolor = getDashColor('green_font');

const ProgressCardTop = ({ campaign, progress }: ProgressCardProps) => {
  const [updateModal, setUpdateModal] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const history = useHistory();
  const [modal, setModal] = useState(false);
  const toggle = () => setModal(!modal);
  const shareUrl = `https://helphopelive.org/campaign/${campaign.campaign_id}/`;

  const onSubmitUpdate = async (data) => {
    try {
      setSaving(true);
      data['campaign_id'] = campaign.campaign_id;
      const payload = data;
      const resp = await postCampaignUpdate(payload, campaign.campaign_id);
      console.log('resp: ', resp);
      setUpdateModal(false);
      const { wordpress_permalink } = campaign;
      if (wordpress_permalink) {
        toast.success(
          <div>
            View your{' '}
            <a
              href={`${import.meta.env.VITE_WP_URL}/campaign/${
                campaign?.campaign_id
              }`}
              target='_blank'
            >
              Campaign Update here!
            </a>
          </div>
        );
      } else {
        toast.success('Campaign update saved!');
      }
      history.push('/campaign/updates');
    } catch (e) {
      console.log('e: ', e);
      setSaving(false);
    }
  };

  const [isCopied, setIsCopied] = useState(false);

  async function copyTextToClipboard(text) {
    if ('clipboard' in navigator) {
      return await navigator.clipboard.writeText(text);
    } else {
      return document.execCommand('copy', true, text);
    }
  }

  // onClick handler function for the copy button
  const handleCopyClick = () => {
    // Asynchronously call copyTextToClipboard
    copyTextToClipboard(shareUrl)
      .then(() => {
        // If successful, update the isCopied state value
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 1500);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  return (
    <>
      <Row className='align-items-center'>
        <Col xl={6} className='mb-3 mb-xl-0'>
          <div className='d-flex'>
            {campaign?.campaign_status === 'Disbursable' && (
              <div className='me-3'>
                <IconVerified />
              </div>
            )}

            <div
              style={{
                fontSize: '26px',
                fontWeight: '600',
                color: fontcolor,
                lineHeight: '1',
              }}
            >
              Campaign in honor of {campaign?.campaign_name}
            </div>
          </div>
        </Col>
        <Col xl={6} className='text-center text-xl-end'>
          <Button color='link' className='text-uppercase' onClick={toggle}>
            {renderIcon('share')}
            <span className='d-md-none d-lg-inline'>Share Campaign</span>
          </Button>
          <Button color='primary' onClick={() => setUpdateModal(true)}>
            Post Campaign Update
          </Button>
        </Col>
      </Row>
      <CampaignUpdateModal
        isOpen={updateModal}
        toggle={() => setUpdateModal(false)}
        onSubmit={onSubmitUpdate}
        saving={saving}
      />
      <Modal centered size='lg' isOpen={modal} toggle={toggle}>
        <ModalHeader tag='h3' className='border-0' toggle={toggle}>
          Share your campaign
        </ModalHeader>
        <ModalBody>
          <p
            className='d-flex justify-content-between align-items-center py-2 ps-4 pe-2 rounded-pill text-break'
            style={{ background: 'rgba(1, 174, 180, 0.1)' }}
          >
            {shareUrl}
            <Button color='link' className='p-1' onClick={handleCopyClick}>
              {isCopied ? 'Copied!' : renderIcon('copy')}
            </Button>
          </p>
          <p>
            You are responsible for complying with the privacy policies and
            terms for any third-party service you share your campaign with.
          </p>
          <p className='d-flex justify-content-center align-items-center'>
            <div className='btn btn-link m-0'>
              <FacebookShareButton url={shareUrl} className='text-uppercase'>
                <FacebookIcon size={32} round />{' '}
                <span className='d-none d-sm-inline'>Facebook</span>
              </FacebookShareButton>
            </div>
            <div className='btn btn-link m-0'>
              <TwitterShareButton
                url={`https://helphopelive.org/campaign/${campaign.campaign_id}/`}
                className='text-uppercase'
              >
                <XIcon size={32} round />{' '}
                <span className='d-none d-sm-inline'>X (Twitter)</span>
              </TwitterShareButton>
            </div>
            <div className='btn btn-link m-0'>
              <LinkedinShareButton
                url={`https://helphopelive.org/campaign/${campaign.campaign_id}/`}
                className='text-uppercase'
              >
                <LinkedinIcon size={32} round />{' '}
                <span className='d-none d-sm-inline'>LinkedIn</span>
              </LinkedinShareButton>
            </div>
          </p>
        </ModalBody>
        <ModalFooter className='justify-content-start border-0'>
          <Button color='outline-dark' onClick={toggle}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};
const ProgressCardBottom = ({ campaign, progress }: ProgressCardProps) => {
  const { goal, total, percentage } = progress;
  
  return (
    <Row>
      <Col>
        <h5 className='mb-2'>
          <span
            style={{
              fontSize: '38px',
              fontWeight: '500',
              color: fontcolor,
            }}
          >
            {toMoneyWhole(String(total))}
          </span>{' '}
          {goal > 1 && (
            <span
              style={{
                fontWeight: '300',
              }}
            >
              /{toMoneyWhole(String(goal))}
            </span>
          )}
        </h5>
        {campaign?.therm_start_date ? (
          <div className='small'>
            Funds raised{' '}
            {format(parseISO(campaign?.therm_start_date), 'MM/dd/yy')} through{' '}
            {format(new Date(), 'MM/dd/yy')}
          </div>
        ) : (
          <div className='small'>
            Total funds raised as of {format(new Date(), 'MM/dd/yy')}
          </div>
        )}
      </Col>
      {goal > 1 && (
        <Col xs={6} className='text-end'>
          <h4
            style={{
              fontSize: '38px',
              fontWeight: '500',
            }}
          >
            {percentage}%
          </h4>
        </Col>
      )}
    </Row>
  );
};
export const ProgressCard = ({ campaign, progress }: ProgressCardProps) => {
  if (progress?.show_progress === false || !progress)
    return (
      <div>
        <ProgressCardTop campaign={campaign} progress={progress} />
        <ProgressCardBottom campaign={campaign} progress={progress} />
      </div>
    );

  const { percentage, goal } = progress;

  return (
    <div>
      <ProgressCardTop campaign={campaign} progress={progress} />
      {goal > 1 && (
        <Progress
          className='my-3'
          style={{
            height: '24px',
          }}
          color='success'
          value={percentage}
        />
      )}
      <ProgressCardBottom campaign={campaign} progress={progress} />
    </div>
  );
};

export const formatCurrencyStr = (n: number) => {
  const s = String(n);
  return formatCurrency(s);
};
